import { z } from 'zod';

/** Product roles are independent of the site/contractor membership that bounds data. */
export const businessRoleSchema = z.enum([
  'hq-staff', 'hq-safety-manager', 'site-staff', 'site-safety-manager', 'contractor-staff',
]);
export type BusinessRole = z.infer<typeof businessRoleSchema>;
export const businessRoleLabels: Record<BusinessRole, string> = {
  'hq-staff': '본사 담당자', 'hq-safety-manager': '본사 안전관리자',
  'site-staff': '사업장 담당자', 'site-safety-manager': '사업장 안전관리자',
  'contractor-staff': '협력업체 담당자',
};
export const featureAccessRoleSchema = z.enum([...businessRoleSchema.options, 'developer']);
export type FeatureAccessRole = z.infer<typeof featureAccessRoleSchema>;
/** Additive account roles never replace the account's site/HQ business duty. */
export const additionalAccountRolesSchema = z.array(z.literal('developer')).max(1);

/** Public product features, not individual read/write capabilities. */
export const managedFeatureCatalog = [
  { id: 'inspection-reporting', label: '점검', kind: 'business' },
  { id: 'tbm', label: 'TBM', kind: 'business' },
  { id: 'incident', label: '재해자 관리', kind: 'business' },
  { id: 'regular-risk', label: '위험성평가', kind: 'business' },
  { id: 'work-permit', label: '비일상작업', kind: 'business' },
  { id: 'equipment-dispatch', label: '차량·장비 작업배차', kind: 'business' },
  { id: 'safety-training', label: '안전보건교육', kind: 'business' },
  { id: 'evacuation-drill', label: '비상대피훈련', kind: 'business' },
  { id: 'chemical-msds-management', label: '화학물질·MSDS 관리대장', kind: 'business' },
  { id: 'heat-index', label: '체감온도 기록', kind: 'business' },
  { id: 'meeting-minutes', label: '회의록', kind: 'business' },
  { id: 'safety-content', label: '안전 콘텐츠 제작', kind: 'business' },
  { id: 'electronic-approval', label: '결재 문서', kind: 'business' },
  { id: 'safety-review', label: '검토·승인', kind: 'review' },
  { id: 'safety-board', label: '게시판', kind: 'business' },
  { id: 'hazard-report', label: '위험요인 신고', kind: 'business' },
  { id: 'system-improvement-report', label: '시스템 개선 신고', kind: 'business' },
  { id: 'product-feedback', label: '시스템 개선 관리', kind: 'developer' },
  { id: 'user-approval', label: '사용자 관리', kind: 'business' },
  { id: 'contractor-management', label: '협력업체', kind: 'business' },
  { id: 'safety-cost', label: '안전관리비', kind: 'business' },
  { id: 'site-settings', label: '사업장 기본정보', kind: 'business' },
  { id: 'inspection-checklists', label: '점검 분류·점검표', kind: 'business' },
  { id: 'organization-chart', label: '조직도', kind: 'business' },
  { id: 'work-location-registry', label: '공종·작업장소', kind: 'business' },
  { id: 'tbm-settings', label: 'TBM 설정', kind: 'business' },
  { id: 'shared-drive', label: '안전보건자료실', kind: 'business' },
  { id: 'system-management', label: '시스템 관리', kind: 'developer' },
] as const;
export type ManagedFeatureId = typeof managedFeatureCatalog[number]['id'];
/** Existing subroutes remain part of their parent feature, not new role-specific permissions. */
const featureAliases: Readonly<Record<string, ManagedFeatureId>> = {
  'inspection-log': 'inspection-reporting',
  'inspection-unresolved': 'inspection-reporting',
  'finding-action': 'inspection-reporting',
};
export function managedFeatureForRoute(route: string): ManagedFeatureId | null {
  return managedFeatureCatalog.find(feature => feature.id === route)?.id ?? featureAliases[route] ?? null;
}
export const managedFeatureIdSchema = z.enum(managedFeatureCatalog.map(row => row.id));
export const featureRoleGrantsSchema = z.record(managedFeatureIdSchema, z.array(featureAccessRoleSchema));
export type FeatureRoleGrants = z.infer<typeof featureRoleGrantsSchema>;

export const featureAccessPolicySchema = z.object({
  schemaVersion: z.literal(1), tenantId: z.literal('kop'), version: z.number().int().positive(),
  grants: featureRoleGrantsSchema,
}).strict().superRefine((policy, context) => {
  for (const feature of managedFeatureCatalog) {
    const roles = policy.grants[feature.id];
    if (new Set(roles).size !== roles.length)
      context.addIssue({ code: 'custom', path: ['grants', feature.id], message: 'Duplicate role' });
    if (feature.kind === 'review' && roles.some(role => !['hq-safety-manager', 'site-safety-manager'].includes(role)))
      context.addIssue({ code: 'custom', path: ['grants', feature.id], message: 'Review requires a safety role' });
    if (feature.kind === 'developer' && roles.some(role => role !== 'developer'))
      context.addIssue({ code: 'custom', path: ['grants', feature.id], message: 'Developer feature requires developer role' });
  }
  if (!policy.grants['system-management'].includes('developer'))
    context.addIssue({ code: 'custom', path: ['grants', 'system-management'], message: 'System management must remain reachable' });
});
export type FeatureAccessPolicy = z.infer<typeof featureAccessPolicySchema>;

export type FeatureAccessSubject = Readonly<{
  role: BusinessRole | null;
  developer: boolean;
}>;

/** Does not grant data access: callers must independently validate tenant/site/contractor scope. */
export function hasFeatureAccess(policy: FeatureAccessPolicy, subject: FeatureAccessSubject, feature: ManagedFeatureId): boolean {
  const grants = policy.grants[feature];
  return Boolean((subject.role && grants.includes(subject.role)) || (subject.developer && grants.includes('developer')));
}

/** Never infer business authority from the legacy all-powerful administrator persona. */
export function mapLegacyBusinessRole(persona: string): BusinessRole | null {
  switch (persona) {
    case 'hq-staff': case 'hq-auditor': return 'hq-staff';
    case 'hq-safety-admin': return 'hq-safety-manager';
    case 'site-staff': case 'safety-staff': return 'site-staff';
    case 'safety-manager': return 'site-safety-manager';
    case 'partner-admin': return 'contractor-staff';
    default: return null;
  }
}

export type FeatureAccessComparison = Readonly<{
  retained: readonly ManagedFeatureId[]; added: readonly ManagedFeatureId[]; removed: readonly ManagedFeatureId[];
  unresolvedRole: boolean;
}>;
export function compareFeatureAccess(
  currentFeatures: readonly ManagedFeatureId[], policy: FeatureAccessPolicy, subject: FeatureAccessSubject,
): FeatureAccessComparison {
  const current = new Set(currentFeatures);
  const next = managedFeatureCatalog.filter(feature => hasFeatureAccess(policy, subject, feature.id)).map(feature => feature.id);
  return {
    retained: next.filter(feature => current.has(feature)), added: next.filter(feature => !current.has(feature)),
    removed: [...current].filter(feature => !next.includes(feature)), unresolvedRole: subject.role === null,
  };
}
